import React from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/actions/collapse.svg";
import { ReactComponent as DevicesSVG } from "../../../../../../../../images/svg/subscriptions/devices.svg";
import {
	AddonApi,
	SubscriptionApi,
	SubscriptionsApi,
	UpdateSubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { drawSubscriptionDetailTitle, getBillingPeriod } from "../../pages/Edit";
import { drawSubscriptionsPriceByType, isDiscountedSubscription } from "../Preview/SubscriptionsPreview";
import { checkIfDisplaySubscription, getTotalMonthlyAddonsPrice } from "./services";

interface Props {
	estimate: SubscriptionsApi;
	form: UseFormReturn<UpdateSubscriptionsApi>;
	addCoupon: () => void;
	setExpanded: (value: boolean) => void;
}

const StickySubscriptionsSummaryDetails = ({ estimate, form, addCoupon, setExpanded }: Props) => {
	const { t } = useTranslation();
	const subscriptions = estimate.subscriptions;
	const organization = useSelector(selectOrganization);
	const {
		formState: { errors },
		register,
	} = form;

	const drawSubscriptionAddons = (item: SubscriptionApi, addons: AddonApi[]) => {
		return (
			addons &&
			addons.map((addon, index) => (
				<div className="row" key={`addon-${index}`}>
					<div className="col-6">
						<p className="text-muted addon-heading">
							{drawSubscriptionDetailTitle(addon.name, addon.quantity || 0)}
						</p>
					</div>
					<div className="col-6 text-end">
						<p className="text-muted addon-heading">
							{FormatMoney(
								addon?.total_price?.amount
									? addon.total_price
									: {
											amount: 0,
											currency: item?.total_money?.currency,
									  }
							)}
							/{getBillingPeriod(item.billing_period)}
						</p>
					</div>
				</div>
			))
		);
	};

	const drawSubscriptions = () => {
		return (
			subscriptions &&
			subscriptions.filter(checkIfDisplaySubscription).map((subscription, index) => (
				<React.Fragment key={`subscription-${index}`}>
					<div className="subscription-summary-divider" />
					<div className="py-1">
						<div className="row">
							<div className="col-6">
								<p className="mb-0">{subscription.family_name}</p>
							</div>
							<div className="col-6 text-end">
								<p className="mb-0">
									{FormatMoney(subscription.total_money)}/
									{getBillingPeriod(subscription.billing_period)}
								</p>
								{getTotalMonthlyAddonsPrice(subscription, organization).amount > 0 && (
									<p style={{ fontSize: "12px" }}>
										+ {FormatMoney(getTotalMonthlyAddonsPrice(subscription, organization))}/mies.
									</p>
								)}
							</div>
						</div>
						{subscription.plan_id && (
							<div className="row">
								<div className="col-6">
									<p className="text-muted addon-heading">{subscription.name}</p>
								</div>
								<div className="col-6 text-end">
									<p className="text-muted addon-heading">
										{FormatMoney(subscription.subscription_money)}/
										{getBillingPeriod(subscription.billing_period)}
									</p>
								</div>
							</div>
						)}
						{subscription.addons && drawSubscriptionAddons(subscription, subscription.addons)}
						{subscription.monthly_sub_item &&
							drawSubscriptionAddons(subscription.monthly_sub_item, subscription.monthly_sub_item.addons)}
					</div>
				</React.Fragment>
			))
		);
	};

	return (
		<div className="subscriptions-payment-summary-details">
			<div className="d-flex align-items-center justify-content-between mb-4" onClick={() => setExpanded(false)}>
				<div className="d-flex align-items-center">
					<DevicesSVG className="me-2" />
					<h5 className="mb-0">{t("lib:common.word.summary")}</h5>
				</div>
				<DropdownSVG className="icon dropdown-icon" />
			</div>
			<div className="subscriptions-payment-summary-card--inside">
				<div className="row">
					<div className="col-6">
						<label className="form-label">{t("modules.subscription.field.app.title")}</label>
					</div>
					<div className="col-6 text-end">
						<label className="form-label">{t("modules.subscription.field.price.title")}</label>
					</div>
				</div>
				{drawSubscriptions()}
				<div className="subscription-summary-divider" />
				<div className="py-3 d-flex align-items-center gap-2 w-100 coupon-code-box">
					<FormInput
						name="coupon_code"
						errors={errors}
						register={register}
						placeholder={"Wprowadź kod boxa"}
					/>
					<Button variant="add" onClick={addCoupon}>
						Zastosuj
					</Button>
				</div>
				<div className="subscription-summary-divider mb-3" />
				{isDiscountedSubscription(estimate.totals) && (
					<>
						<div className="row">
							<div className="col-6">
								<p className="text-muted addon-heading">
									{t("modules.subscription.field.price_without_discount.title")}
								</p>
							</div>
							<div className="col-6 text-end">
								{drawSubscriptionsPriceByType(estimate.totals, "sub_total")}
							</div>
						</div>
						<div className="row">
							<div className="col-6">
								<p className="text-muted addon-heading">
									{t("modules.subscription.field.discount_value.title")}
								</p>
							</div>
							<div className="col-6 text-end">
								{drawSubscriptionsPriceByType(estimate.totals, "discount")}
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default StickySubscriptionsSummaryDetails;
